/**
 * Tipos da aba Cenas, espelhando os records de {@code MundoDTO} no backend.
 *
 * <p>Ficam à parte de `tipos.ts` porque a cena não é uma ficha do mundo: ela referencia
 * as outras e não é referenciada por nenhuma. Pôr 'cenas' na união `Colecao` faria o
 * esquema das dez coleções ter de responder por uma décima primeira que não tem
 * listagem, nem ficha genérica, nem seletor.
 */
import type { Colecao } from '../tipos';

export type TipoDeCena = 'LOCAL' | 'NPC' | 'CRIATURA' | 'COLETAVEL' | 'EVENTO';

/** Na ordem em que os filtros aparecem: o lugar primeiro, depois quem o habita. */
export const TIPOS_DE_CENA: TipoDeCena[] = [
  'LOCAL',
  'NPC',
  'CRIATURA',
  'COLETAVEL',
  'EVENTO',
];

/** As cores do protótipo, iguais nas duas visões. */
export const COR_DO_TIPO: Record<TipoDeCena, string> = {
  LOCAL: '#3FA0A0',
  NPC: '#5B8FB9',
  CRIATURA: '#B04A45',
  COLETAVEL: '#4E9C6B',
  EVENTO: '#C0703C',
};

/** O rótulo do badge é a palavra do time — a mesma que está no menu do mundo. */
export const ROTULO_DO_TIPO: Record<TipoDeCena, string> = {
  LOCAL: 'LOCAL',
  NPC: 'NPC',
  CRIATURA: 'CRIATURA',
  COLETAVEL: 'COLETÁVEL',
  EVENTO: 'EVENTO',
};

/**
 * A coleção que cada tipo exige. Espelha {@code TipoDeCena.alvo()} no backend — se
 * um dia divergirem, o servidor é quem manda: ele recusa o vínculo errado.
 */
export const ALVO_DO_TIPO: Record<TipoDeCena, Colecao | null> = {
  LOCAL: null,
  NPC: 'npcs',
  CRIATURA: 'criaturas',
  COLETAVEL: 'itens',
  EVENTO: 'eventos',
};

/** O que a ficha da cena pede, em palavras, quando o vínculo falta. */
export const NOME_DO_ALVO: Record<TipoDeCena, string> = {
  LOCAL: '',
  NPC: 'NPC',
  CRIATURA: 'criatura',
  COLETAVEL: 'item',
  EVENTO: 'evento',
};

/** Referência a uma cena — só o que um chip ou uma opção precisa mostrar. */
export interface ResumoDeCena {
  id: number;
  nome: string;
}

export interface OpcaoDaCena {
  id?: number;
  texto: string;
  destino?: ResumoDeCena | null;
}

/** Um vínculo com uma ficha do mundo, como o backend o devolve. */
export interface VinculoDoMundo {
  colecao: Colecao;
  id: number;
  nome: string;
  imagem?: string;
  nota?: string;
}

export interface Cena {
  id: number;
  nome: string;
  tipo: TipoDeCena;
  codigo?: string;
  area?: VinculoDoMundo | null;
  local?: VinculoDoMundo | null;
  /** A ficha que o tipo exige, já resolvida pelo servidor. */
  alvo?: VinculoDoMundo | null;
  alvoColecao?: Colecao | null;
  quest?: VinculoDoMundo | null;
  narrativa?: string;
  descImagem?: string;
  imagem?: string;
  recompensas?: string;
  opcoes: OpcaoDaCena[];
  semVinculo: boolean;
  autoria: {
    criadoPor?: string;
    criadoEm?: string;
    atualizadoPor?: string;
    atualizadoEm?: string;
  };
}

/**
 * Um nó da árvore — que é **o conteúdo da área**, e não uma linha da tabela `cena`.
 *
 * Tudo é cena: a Forja é uma cena, o Icarus é uma cena, o lobo é uma cena. Eles estão
 * aqui porque existem no mundo. A linha de `cena` é o que se *escreve* sobre o nó, e
 * por isso `id` é nulo enquanto ninguém escreveu nada.
 *
 * Quem identifica o nó são `alvoColecao` + `alvoId`, nunca nulos.
 */
export interface CenaNaArvore {
  /** O id da linha de `cena`. Nulo no nó ainda não escrito. */
  id?: number | null;
  nome: string;
  tipo: TipoDeCena;
  codigo?: string;
  /** Hoje sempre nulo: o nó É a ficha, e repetir daria "Icarus → Icarus". */
  alvo?: string | null;
  alvoColecao?: Colecao | null;
  alvoId?: number | null;
  /** Se já existe texto escrito para este nó. Substituiu `semVinculo`. */
  escrita: boolean;
  /** O local onde o nó está, quando diz algo que o nome não disse. */
  local?: string | null;
  localId?: number | null;
  /** Para onde as escolhas levam. Usado pela visão Árvore. */
  destinos: (number | null)[];
  filhas: CenaNaArvore[];
}

export interface AreaComCenas {
  id: number;
  nome: string;
  cenas: CenaNaArvore[];
}

export interface RegiaoComCenas {
  id: number;
  nome: string;
  corMapa?: string;
  icone?: string;
  areas: AreaComCenas[];
}

export interface ContagemDeCenas {
  cenas: number;
  areas: number;
  ligacoes: number;
  /**
   * Conteúdo que existe no mundo e ainda não tem texto.
   *
   * Substituiu `semVinculo`, e mudou de natureza junto com a árvore: aquilo era cena
   * apontando para o vazio, uma inconsistência; isto é trabalho combinado, e o número
   * cai conforme o time escreve.
   */
  aEscrever: number;
}

/*
 * `CandidatoDeCena` saiu.
 *
 * Ele era o que a área tinha e ainda não virara cena, enquanto a árvore listava a
 * tabela `cena` e o conteúdo ficava de fora. Agora a árvore É o conteúdo: candidato e
 * nó viraram a mesma linha, e o que os distinguia virou `CenaNaArvore.escrita`.
 */

export interface PaginaDeCenas {
  regioes: RegiaoComCenas[];
  /** O prólogo: o que acontece antes de o jogador estar em lugar algum. */
  semArea: CenaNaArvore[];
  contagem: ContagemDeCenas;
}
